import { ComponentProps, useLayoutEffect } from 'react'
import { SceneLineMarker } from '@scene'
import { v4 } from 'uuid'
import { SceneElement, useControlStore } from '@editorStores'

/**
 * Adds temporary line overlays to the scene.
 * Useful for highlighting outlines, edges, or groups of elements without mutating the model.
 */
const useSceneLineMarker = ({
  lines,
  enabled,
  color,
  lineWidth,
  dashed,
  dashSize,
  gapSize,
  dashScale,
  renderOrder,
  disableDepthTest,
}: {
  lines: ComponentProps<typeof SceneLineMarker>['lines']
  enabled: boolean
  color?: string
  lineWidth?: number
  dashed?: boolean
  dashSize?: number
  gapSize?: number
  dashScale?: number
  renderOrder?: number
  disableDepthTest?: boolean
}) => {
  const addSceneMarker = useControlStore(state => state.addSceneElement)
  const removeSceneMarker = useControlStore(state => state.removeSceneElement)

  useLayoutEffect(() => {
    if (enabled && lines.length > 0) {
      const id = v4()

      const properties: ComponentProps<typeof SceneLineMarker> = {
        lines,
        color,
        lineWidth,
        dashed,
        dashSize,
        gapSize,
        dashScale,
        renderOrder,
        disableDepthTest,
      }

      const sceneMarker: SceneElement<ComponentProps<typeof SceneLineMarker>> = {
        id,
        properties,
        component: SceneLineMarker,
      }

      addSceneMarker(sceneMarker)

      return () => removeSceneMarker(id)
    }
  }, [
    addSceneMarker,
    color,
    dashScale,
    dashSize,
    dashed,
    disableDepthTest,
    enabled,
    gapSize,
    lineWidth,
    lines,
    removeSceneMarker,
    renderOrder,
  ])
}

export default useSceneLineMarker
